/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { Grid } from '@mui/material';

import {
	Button,
	DateSelect,
	ModalConfirmation,
	Pager,
	Popup,
	ProgressBar,
	Select,
	Typography,
} from 'design_system/src';

import { ReactComponent as Moto } from 'design_system/src/static/icons/motorcycle.svg';
import { ReactComponent as AddCircle } from 'design_system/src/static/icons/add-circle.svg';
import { ReactComponent as Signal } from 'design_system/src/static/icons/radio-signal.svg';
import { ReactComponent as NoSignal } from 'design_system/src/static/icons/no-radio-signal.svg';
import { ReactComponent as Home } from 'design_system/src/static/icons/temple.svg';
import { ReactComponent as Hotspot } from 'design_system/src/static/icons/gps.svg';
import { ReactComponent as Account } from 'design_system/src/static/icons/user-circle.svg';
import { ReactComponent as Action } from 'design_system/src/static/icons/more-v.svg';
import { ReactComponent as Warning } from 'design_system/src/static/icons/warning-circle.svg';

import { toasterOptions } from 'models/shared/enums/toaster.enum';
import {
	FetchRecoveries,
	FetchRoutes,
	updateRoute,
} from 'models/repossession/controllers/repossession.controller';

import { Route } from 'models/repossession/interfaces/route.interface';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import {
	RouteStatus,
	RouteStatusValues,
} from 'models/repossession/enums/route.enums';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import { PaginatedDocuments } from 'models/shared/types/pagination';
import { InterestPointType } from 'models/repossession/enums/repossession.enums';
import { RecoveryStatus } from 'models/repossession/enums/recovery.enums';
import useDateFilters from 'hooks/useDatesFilters';
import moment from 'moment';
import motoLoading from '../../../static/icons/loading_moto.gif';
import { repossessionContext } from '../RepossessionsContext';
import {
	RepossessionTabs,
	RepossessionTabsValues,
} from '../Repossessions.constants';

const Routes = () => {
	const navigate = useNavigate();

	const { viewTab, setViewTab, ozonios } = useContext(repossessionContext);

	const [actualPage, setActualPage] = useState<number>(1);
	const [openAction, setOpenAction] = useState<Route>();

	const [routes, setRoutes] = useState<(Route & { totals: any })[]>([]);
	const { datesFilter, hasDatesFilter, setDatesFilter, setHasDatesFilter } =
		useDateFilters();
	const [ozonioFilter, setOzonioFilter] = useState([]);
	const [statusFilter, setStatusFilter] = useState([
		RouteStatus.pending,
		RouteStatus.inProgress,
	]);
	const [count, setCount] = useState(0);
	const [totalPages, setTotalPages] = useState<number>(0);

	const [openConfirm, setOpenConfirm] = useState(false);

	const fetchRoutes = () => {
		FetchRoutes(
			actualPage,
			statusFilter,
			hasDatesFilter
				? {
						start: datesFilter.initial,
						end: datesFilter.last,
				  }
				: undefined,
			ozonioFilter,
		).then((resp: PaginatedDocuments<Route>) => {
			const newRoutes = resp.data.map((route) => {
				let routeSignal = 0;
				let routeNoSignal = 0;
				let address = 0;
				let hotspots = 0;
				let account = 0;
				let completed = 0;
				const finalRoute = { ...route, totals: {} };
				finalRoute.recoveries.forEach((recovery) => {
					if (recovery.interestPoint?.type === InterestPointType.address) {
						address += 1;
					} else if (
						recovery.interestPoint?.type === InterestPointType.hotspot
					) {
						hotspots += 1;
					} else if (
						recovery.interestPoint?.type === InterestPointType.geolocation
					) {
						account += 1;
					} else if (
						recovery.interestPoint?.type === InterestPointType.signalVehicle ||
						recovery.interestPoint?.type === InterestPointType.signalTileVehicle
					) {
						routeSignal += 1;
					} else {
						routeNoSignal += 1;
					}
					if (recovery.status === RecoveryStatus.recovered) {
						completed += 1;
					}
				});
				// eslint-disable-next-line no-param-reassign
				finalRoute.totals = {
					signal: routeSignal,
					noSignal: routeNoSignal,
					address,
					hotspots,
					account,
					completed,
				};
				return finalRoute;
			});
			setRoutes(newRoutes);
			setTotalPages(resp.pages);
			setCount(resp.count);
		});
	};

	const selectRuta = (ruta: Route) => {
		navigate(`/route/${ruta._id}`);
	};
	const cancelRoute = () => {
		if (openAction) {
			updateRoute(openAction._id, { status: RouteStatus.canceled })
				.then(() => {
					fetchRoutes();
					toasterNotification({
						msg: 'Se cancelo la ruta',
						toasterType: toasterOptions.success,
						style: 'light',
					});
				})
				.catch((e) => {
					commonErrorHandler(e);
				})
				.finally(() => {
					setOpenAction(undefined);
					setOpenConfirm(false);
				});
		}
	};

	useEffect(() => {
		fetchRoutes();
	}, [hasDatesFilter, datesFilter, ozonioFilter, statusFilter, actualPage]);

	return (
		<div className="dim_100_per flex_content_col inner_container bg_neutral_300">
			<div className="w_100_per bg_neutral_0 flex_header p_x_lg p_y_md">
				<div className="display_flex flex_align_center flex_justify_between m_b_md">
					<Typography
						scale="heading2"
						weight="600"
						className="text_primary_300"
					>
						Rutas
					</Typography>
					<div className="display_flex flex_align_center flex_gap_sm p_x_xs p_y_xxs bg_neutral_300 br_xs">
						{Object.entries(RepossessionTabsValues).map(([tab, title]) => (
							<Typography
								scale="medium"
								key={tab}
								weight={tab === viewTab ? '600' : '400'}
								textColor={tab === viewTab ? 'neutral_1000' : 'neutral_700'}
								className={`p_x_md p_y_xxs br_xs cursor_pointer ${
									tab === viewTab ? 'bg_neutral_0 shadow_soft' : ''
								}`}
								onClick={() => setViewTab(tab as RepossessionTabs)}
							>
								{title}
							</Typography>
						))}
					</div>
				</div>
				<div className="display_flex flex_align_center flex_gap_md">
					<Select
						fixedPlaceholder
						variant="checkbox"
						placeholder="Estado"
						options={Object.entries(RouteStatusValues).map(([key, value]) => ({
							label: value.title,
							value: key,
						}))}
						value={statusFilter}
						onChange={(e: any) => {
							setStatusFilter(e.target.value);
						}}
						className=""
					/>
					<Select
						name="sortVehicles"
						options={[{ label: 'Todos los encargados', value: '' }, ...ozonios]}
						className="w_20_vp"
						placeholder="Selecciona un encargado"
						multiple
						value={ozonioFilter}
						onChange={(e: any) => setOzonioFilter(e.target.value)}
					/>
					<DateSelect
						value={datesFilter}
						hasValue={hasDatesFilter}
						setHasValue={setHasDatesFilter}
						placeholder="Fecha de creación"
						setValue={(e: any) => {
							setDatesFilter(e);
							setHasDatesFilter(true);
						}}
					/>
				</div>
			</div>
			{!routes ? (
				<div className="loader_map flex_center">
					<Typography
						weight="600"
						scale="medium"
						textColor="neutral_0"
						className="m_b_xs"
					>
						Cargando la información...
					</Typography>
					<img src={motoLoading} alt="" className="h_xxxl" />
				</div>
			) : null}
			<div className="flex_body p_x_xxxl overflow_x_hidden bg_neutral_300">
				<Grid
					container
					direction="row"
					spacing={3}
					className="h_100_per p_t_md spaced"
				>
					{routes?.map((route, i) => (
						<Grid item xs={6} sm={3} md={3} key={i}>
							<div className="br_sm bg_neutral_0">
								<div className="display_flex flex_justify_between p_md p_b_xs border_b_solid border_neutral_300 border_1 flex_align_center">
									<div>
										<Typography
											weight="600"
											scale="medium"
											textColor="neutral_1000"
											className=""
										>
											{moment
												.utc(route.routeDate)
												.locale('es')
												.format('dddd, D MMMM')}
										</Typography>
										<Typography
											weight="400"
											scale="small"
											textColor="neutral_600"
											className=""
										>
											{route.team.map((ozonio) => ozonio.name).join(', ')}
										</Typography>
									</div>
									{route.status === RouteStatus.pending ||
									route.status === RouteStatus.inProgress ? (
										<div className="pos_relative">
											<Action
												className="dim_md cursor_pointer border_transparent"
												tabIndex={0}
												role="button"
												onKeyDown={() =>
													setOpenAction(openAction ? undefined : route)
												}
												onClick={() =>
													setOpenAction(openAction ? undefined : route)
												}
											/>
											<Popup
												open={openAction?._id === route._id}
												setOpen={(action: boolean) =>
													setOpenAction(action ? route : undefined)
												}
												placementX="left-inset"
												className="bg_neutral_100 br_xxs"
											>
												<div
													className="p_x_md p_y_xs action_option cursor_pointer"
													tabIndex={0}
													role="button"
													onKeyDown={() => setOpenConfirm(true)}
													onClick={() => setOpenConfirm(true)}
												>
													<Typography
														scale="medium"
														weight="400"
														className="text_no_break"
													>
														Cancelar ruta
													</Typography>
												</div>
											</Popup>
										</div>
									) : null}
								</div>
								<div className="p_md p_y_xs border_b_solid border_neutral_300 border_1">
									<div className="display_flex flex_gap_xs flex_align_center">
										<Moto className="dim_md" />
										<Typography
											weight="400"
											scale="medium"
											textColor="neutral_1000"
											className=""
										>
											{route.recoveries.length} puntos de interes a visitar
										</Typography>
									</div>
									<div className="flex_center">
										<Typography
											weight="600"
											scale="small"
											textColor={
												RouteStatusValues[route.status].textColor as any
											}
											className={`bg_${
												RouteStatusValues[route.status].color
											}_nocontrast br_xl p_y_xxs p_x_sm m_y_xs`}
										>
											{RouteStatusValues[route.status].title}
										</Typography>
									</div>
									<div className="display_flex flex_justify_between">
										<div className="display_flex flex_gap_xs flex_align_center text_green_300">
											<Signal className="dim_md" />
											<Typography weight="600" scale="medium" className="">
												{route.totals?.signal ?? 0} con señal
											</Typography>
										</div>
										<div className="display_flex flex_gap_xs flex_align_center text_red_300">
											<NoSignal className="dim_md" />
											<Typography weight="600" scale="medium" className="">
												{route.totals?.noSignal ?? 0} sin señal
											</Typography>
										</div>
									</div>
									<div className="display_flex flex_justify_between m_t_xs">
										<div className="display_flex flex_gap_xs flex_align_center text_yellow_100">
											<Home className="dim_md" />
											<Typography weight="600" scale="medium" className="">
												{route.totals?.address ?? 0} Domicilio
											</Typography>
										</div>
										<div className="display_flex flex_gap_xs flex_align_center text_primary_300">
											<Hotspot className="dim_md" />
											<Typography weight="600" scale="medium" className="">
												{route.totals?.hotspots ?? 0} Hotspots
											</Typography>
										</div>
									</div>
									<div className="display_flex flex_justify_between m_t_xs">
										<div className="display_flex flex_gap_xs flex_align_center text_primary_300">
											<Account className="dim_md" />
											<Typography weight="600" scale="medium" className="">
												{route.totals?.account ?? 0} Ingreso a mi cuenta
											</Typography>
										</div>
									</div>
								</div>
								<div className="p_md">
									<Typography
										weight="400"
										scale="small"
										textColor="neutral_600"
										className=""
									>
										Recuperaciones completadas
									</Typography>
									<div className=" p_y_xs display_flex flex_align_center flex_gap_md m_b_xs">
										<Typography
											scale="small"
											weight="600"
											textColor="primary_300"
											className=""
										>
											{route.totals?.completed}/{route.recoveries?.length}{' '}
										</Typography>
										<ProgressBar
											containerClassName="bg_neutral_400 h_sm"
											fillClassName="bg_primary_300 h_sm"
											percentage={
												((route.totals?.completed ?? 0) * 100) /
												route.recoveries.length
											}
										/>
									</div>
									<Button
										scale="small"
										className="w_100_per"
										onClick={() => selectRuta(route)}
									>
										Ver ruta
									</Button>
								</div>
							</div>
						</Grid>
					))}
				</Grid>
			</div>
			<div className="display_flex flex_align_center flex_justify_between flex_footer bg_neutral_0 p_x_xxxl p_y_md">
				<Button
					scale="small"
					icon={<AddCircle />}
					className="w_30_per"
					onClick={() => setViewTab(RepossessionTabs.repossession)}
				>
					Crear nueva ruta
				</Button>
				<Pager
					totalPages={totalPages}
					setPage={setActualPage}
					page={actualPage}
					count={count}
					className="w_fit"
				/>
			</div>
			<ModalConfirmation
				openConfirmation={openConfirm}
				handleClose={() => {
					setOpenConfirm(false);
				}}
				icon={<Warning className="text_primary_300" />}
				isConfirmationLoading={false}
				widthDesktop={40}
				actions={[
					{
						label: 'Volver',
						color: 'grey',
						action: () => {
							setOpenConfirm(false);
						},
					},
					{
						label: 'Cancelar ruta',
						color: 'primary',
						action: () => {
							cancelRoute();
						},
					},
				]}
				title={
					<Typography scale="large" weight="400">
						¿Estás seguro de que{' '}
						<Typography scale="large" weight="600" component="span">
							deseas cancelar esta ruta?
						</Typography>
					</Typography>
				}
				subtitle={
					<Typography scale="medium" weight="400" textColor="neutral_700">
						Una vez cancelada no se podra restaurar la ruta y debera ser creada
						nuevamente
					</Typography>
				}
			/>
		</div>
	);
};

export default Routes;
